import { useAuditStore } from './useAuditStore';
import { clearConfirmDrafts, useOnboardingStore } from './useOnboardingStore';
import { useProcessingStore } from './useProcessingStore';
import { useQuestionnaireStore } from './useQuestionnaireStore';
import { useReportStore } from './useReportStore';
import { clearActiveAnalysis } from '../hooks/useAnalysisConnection';

/**
 * Wipe every per-user store back to its initial state.
 *
 * Called whenever the authenticated identity changes (sign-out, account
 * switch). Without this, switching from user A to user B leaves user A's
 * connection statuses / uploaded files / audit data / report tabs / etc.
 * visible until each view manually re-fetches — and some views never do.
 *
 * `clearActiveAnalysis()` also wipes the `active_analysis` localStorage
 * entry that the SSE recovery flow uses, so the new user doesn't get
 * yanked into the previous user's in-flight analysis.
 *
 * `useThemeStore` is intentionally NOT reset — theme is user-agnostic.
 */
export function resetUserScopedStores(): void {
  useAuditStore.getState().reset();
  // Holds the business profile inferred from the previous user's connected
  // data — the one store here it would be actively harmful to carry over.
  useOnboardingStore.getState().reset();
  useProcessingStore.getState().reset();
  useQuestionnaireStore.getState().reset();
  useReportStore.getState().reset();

  try {
    clearActiveAnalysis();
  } catch {
    // Best-effort: localStorage can throw in private/embedded contexts.
  }

  try {
    if (typeof window !== 'undefined') {
      sessionStorage.removeItem('analysis_chat_id');
      // The onboarding chat stamped by /collect. Left behind, the next user
      // on this tab would try to collect into the previous user's chat — the
      // server 404s it (they don't own it), but the flow dies on the error.
      sessionStorage.removeItem('collect_chat_id');
      // Unsaved Confirm edits describe the previous user's business.
      clearConfirmDrafts();
    }
  } catch {
    /* noop */
  }
}
